import { notification, Table } from 'antd';
import { TablePaginationConfig } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { IPaginatedListProps, IWithKey } from '../models';
import ActionTypes from './actionTypes';
import createReducer, { paginatedListInitialState } from './reducer';

const PaginatedList = <T extends IWithKey>(props: IPaginatedListProps<T>) => {
  const { endpoint, pageSize, columns, getAPIRef, searchQuery, startDate, endDate } = props;
  const rowKey = '_id';
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [state, dispatch] = useReducer(
    createReducer<T>(),
    paginatedListInitialState<T>()
  );

  // Function to fetch data from the endpoint
  const fetchData = useCallback(
    async (
      limit: number = 10,
      page: number = 1,
      sortOrder?: string,
      sortColumn?: string,
    ) => {
      dispatch({
        type: ActionTypes.FETCH_DATA_REQUEST,
      });

      try {
        const response = await axios.get(endpoint, {
          params: { 
            limit, 
            page, 
            sortOrder, 
            sortColumn, 
            search: searchQuery,
            startDate, 
            endDate 
          },
        });

        // Dispatch the data fetch success action
        dispatch({
          payload: {
            current: page,
            dataSource: response.data.docs,
            pageSize: limit,
            total: response.data.totalDocs,
          },
          type: ActionTypes.FETCH_DATA_SUCCESS,
        });

        // Gọi callback với toàn bộ response
        if (props.onDataLoaded) {
          props.onDataLoaded(response.data);
        }
      } catch (error) {
        dispatch({
          type: ActionTypes.FETCH_DATA_FAIL,
        });

        notification.error({
          message: 'Something went wrong',
        });
        console.error(error); // Logging the error for debugging
      }
    },
    [endpoint, searchQuery, startDate, endDate] // Removed props.onDataLoaded to prevent unnecessary re-renders
  );

  

  // Handle table changes like sorting and pagination
  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig, filters: any, sorter: any) => {
      const limit = pagination.pageSize || 10;
      const current = pagination.current || 1;
      const sortOrder = sorter.order || 'ascend';
      const sortColumn = sorter.field || '';

      fetchData(limit, current, sortOrder, sortColumn);
    },
    [fetchData]  // Chỉ cần fetchData trong dependencies
  );

  // Debounced fetch function to prevent too many API calls
  const debouncedFetchData = useCallback((limit: number, page: number) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchData(limit, page);
    }, 300); // 300ms debounce
  }, [fetchData]);

  // Effect to fetch data on initial render or when dependencies change
  useEffect(() => {
    debouncedFetchData(pageSize || 10, 1);
    
    // Cleanup timeout on unmount
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [debouncedFetchData, pageSize]);

  // Exposing the refetchData function via getAPIRef if provided
  if (getAPIRef) {
    (getAPIRef as any).current = {
      refetchData: () => {
        const { pagination } = state;
        const current = pagination ? pagination.current || 1 : 1;
        fetchData(pageSize || 10, current);
      },
    };
  }

  return (
    <>
      <Table<T>
        {...state}
        columns={columns}
        onChange={handleTableChange}
        rowKey={(record: T) => record[rowKey]}
      />
    </>
  );
};

export default PaginatedList;
